/* ═══════════════════════════════════════════════════════════════════
   [D-697] Позначки редакторів v-1…v-5 на функціях, дозволах, наборах і темах дашборду.

   OWNER 21.10: «простав на кожній помітки версій (v-1/v-2/v-3/v-4/v-5) щоб коли я буду вмикати якусь із функцій щоб я розумів до
   якого редактора це відноситься». Назви - як домовлено 08.10:
   v-1 - інлайн-кнопки бота · v-2 - Хаб · v-3 - «🎬 Без спецефектів» · v-4 - «✨ Зі спецефектами» (Посилення) · v-5 - «🎬 Студія».

   Звідки що (звірено з кодом 21.10): розділи довідника bot_* - бот (v-1), окрім кнопок редакторів (btn_plain - v-3, btn_fx - v-4,
   btn_studio - v-5, btn_hub - v-2; як у bot/versions.py KEYS); hub і apps - Хаб (v-2); editor (ed_*) - v-3; hyper: v5_* і st_* - Студія
   (v-5); решта hyper - «Посилення»: є і в v-4 (HyperPage), і в Студії; лише в v-4 - V4_ONLY, лише в Студії - V5_ONLY. pcapp - PC-App.
   Нова функція: v5_* / st_* / ed_* / bot_* / hub_* - підхопиться сама; нова спільна «Посилення» без префікса - «v-4 · v-5».
   ═══════════════════════════════════════════════════════════════════ */
import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

export const VER_COL = { 'v-1': '#90a4ae', 'v-2': '#b388ff', 'v-3': '#69f0ae', 'v-4': '#ffb74d', 'v-5': '#4dd0e1', 'PC': '#f48fb1' };
export const VER_NAME = { 'v-1': 'інлайн-кнопки бота', 'v-2': 'Хаб', 'v-3': '«🎬 Без спецефектів»', 'v-4': '«✨ Зі спецефектами» (Посилення)',
                          'v-5': '«🎬 Студія»', 'PC': 'PC-App (програма на комп\'ютер)' };
const BOT_EDITOR = { btn_here: ['v-1'], btn_plain: ['v-3'], btn_fx: ['v-4'], btn_studio: ['v-5'], btn_hub: ['v-2'] };
// «Посилення» лише на екрані v-4 (HyperPage) або лише в Студії (звірено з кодом мініаппа 21.10)
const V4_ONLY = new Set(['dub', 'tokens_buy', 'fx_shape', 'fx_freq', 'fx_bgimg', 'fx_substyle']);
const V5_ONLY = new Set(['fx_layouts', 'fx_broll', 'fx_info', 'fx_breaks', 'fx_accent']);

export function verOf(key, area) {
  const k = String(key || ''), a = String(area || '');
  if (BOT_EDITOR[k]) return BOT_EDITOR[k];
  if (a.startsWith('bot_') || k.startsWith('btn_')) return ['v-1'];
  if (a === 'hub' || a === 'apps' || k.startsWith('hub_') || k.startsWith('app_')) return ['v-2'];
  if (a === 'editor' || k.startsWith('ed_')) return ['v-3'];
  if (a === 'pcapp' || k.startsWith('pc_')) return ['PC'];
  if (k.startsWith('v5_') || k.startsWith('st_')) return ['v-5'];
  if (a === 'hyper') return V4_ONLY.has(k) ? ['v-4'] : V5_ONLY.has(k) ? ['v-5'] : ['v-4', 'v-5'];
  return [];
}
// розділ довідника - які редактори в ньому є
export function verOfArea(area, keys) {
  const s = new Set(); (keys || []).forEach(k => verOf(k, area).forEach(v => s.add(v)));
  if (!s.size) verOf('', area).forEach(v => s.add(v));
  return ['v-1', 'v-2', 'v-3', 'v-4', 'v-5', 'PC'].filter(v => s.has(v));
}
export const verText = (vs) => (vs && vs.length ? vs.join(' · ') : '');

/* маленькі кольорові позначки; title - підказка, що це за редактор */
export function VerChips({ vers, k, area, size = 'sm', sx }) {
  const vs = vers || verOf(k, area);
  const [mv, setMv] = React.useState(null);   // [D-796] відкрите вікно «які моделі»
  if (!vs.length) return null;
  const fs = size === 'xs' ? 9 : 10;
  return (
    <Box component="span" data-ver={vs.join(',')} sx={{ display: 'inline-flex', gap: .4, flexWrap: 'wrap', verticalAlign: 'middle', ...(sx || {}) }}>
      {mv && <VerModelsDialog v={mv} onClose={() => setMv(null)} />}
      {vs.map(v => (
        <Box key={v} component="span" title={v + ' - ' + (VER_NAME[v] || '') + (VER_MODELS[v] ? ' · натисніть: які моделі ШІ і де баланс' : '')}
             data-ver-chip={v}
             onClick={VER_MODELS[v] ? (e) => { e.stopPropagation(); e.preventDefault(); setMv(v); } : undefined}
             sx={{ px: .55, py: .05, borderRadius: .8, border: '1px solid ' + VER_COL[v], bgcolor: VER_COL[v] + '22', lineHeight: 1.35,
                   cursor: VER_MODELS[v] ? 'pointer' : 'default', ...(VER_MODELS[v] ? { boxShadow: '0 0 0 1px ' + VER_COL[v] + '55' } : {}) }}>
          <Typography component="span" sx={{ fontSize: fs, fontWeight: 900, color: VER_COL[v], letterSpacing: .2 }}>{v}{VER_MODELS[v] ? ' 🤖' : ''}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* одна рядкова легенда - що означає кожна позначка */
export function VerLegend({ sx }) {
  return (
    <Box data-ver-legend="1" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ...(sx || {}) }}>
      {['v-1', 'v-2', 'v-3', 'v-4', 'v-5', 'PC'].map(v => (
        <Box key={v} sx={{ display: 'inline-flex', alignItems: 'center', gap: .5 }}>
          <VerChips vers={[v]} size="xs" />
          <Typography component="span" sx={{ fontSize: 11, color: '#8fa3b8' }}>{VER_NAME[v]}</Typography>
        </Box>
      ))}
    </Box>
  );
}


/* [D-796] OWNER 31.10: «розпиши які моделі використовуються у v-4 щоб я розумів на яких рахунках треба відслідковувати баланс.
   Ще на кнопці в дашборді додай цю інформацію про моделі щоб я завжди міг легко згадати де і що». Натиск на позначку v-4 будь-де
   в дашборді → вікно: рахунок → що на ньому працює. Звірено з кодом воркерів 31.10 (motion_titles, dubbing, ai_analyze,
   video_process.translate_srt, segmentation, head_worker). Модель у ланцюжку може переставити сторож моделей (D-794) - це видно в звіті сторожа. */
const VER_MODELS = {
  'v-4': [
    { acc: 'Anthropic (Claude)', bal: 'console.anthropic.com → Billing', col: '#d4a27f', paid: true,
      what: ['Claude Haiku 5.5 (запасна - Haiku 4.5): акценти й ключові репліки в субтитрах',
             'заголовки-перебивки', 'слова для пошуку фонових картинок', 'переклад титрів',
             'Claude Haiku 4.5 - друга запасна для опису, назви, хештегів і перекладу субтитрів'] },
    { acc: 'OpenAI', bal: 'platform.openai.com → Billing', col: '#10a37f', paid: true,
      what: ['gpt-4o-mini: переклад тексту для озвучки (крок перед голосом)',
             'переклад субтитрів, коли мова відео інша', 'запасна для опису, назви, хештегів'] },
    { acc: 'Google Gemini', bal: 'aistudio.google.com → Billing (якщо ключ платний)', col: '#4285f4', paid: true,
      what: ['gemini-flash-latest: опис, назва, хештеги (основна)'] },
    { acc: 'Replicate', bal: 'replicate.com → Billing', col: '#ff6b6b', paid: true,
      what: ['лише «якісна» озвучка голосом автора: MiniMax Speech 2.6 HD', 'клон голосу MiniMax (раз на юзера)'] },
    { acc: 'Pexels', bal: 'безкоштовно - лише ліміт запитів', col: '#05a081', paid: false,
      what: ['фонові картинки за словами від Claude'] },
    { acc: 'Безкоштовно, без рахунку', bal: 'наші сервери / Microsoft', col: '#90a4ae', paid: false,
      what: ['faster-whisper small: розпізнавання мовлення', 'edge-tts (Microsoft): голос перекладу «безкоштовно»',
             'YuNet: пошук обличчя для кружечка', 'rembg u2net: вирізка автора з фону'] },
  ],
};

export function VerModelsDialog({ v, onClose }) {
  const rows = VER_MODELS[v] || [];
  return (
    <Dialog open={!!v} onClose={onClose} onClick={(e) => e.stopPropagation()} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#121a24', color: '#e6edf3', borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 900, fontSize: 17, pb: .5 }}>
        🤖 {v} {VER_NAME[v] || ''} - які моделі ШІ працюють і де дивитись баланс
      </DialogTitle>
      <DialogContent data-ver-models={v}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.2, mt: .5 }}>
          {rows.map(r => (
            <Box key={r.acc} sx={{ border: '1px solid ' + r.col + '66', bgcolor: r.col + '14', borderRadius: 1.5, p: 1.2 }}>
              <Typography sx={{ fontWeight: 900, fontSize: 14, color: r.col }}>{r.paid ? '💳 ' : '🆓 '}{r.acc}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', mb: .6 }}>{r.bal}</Typography>
              {r.what.map(w => <Typography key={w} sx={{ fontSize: 12.5, lineHeight: 1.35, mb: .35 }}>• {w}</Typography>)}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', mt: 1.2 }}>
          💳 - платний рахунок, стежити за балансом. Якщо гроші скінчаться: Claude / OpenAI / Gemini підхоплюють одна одну (опис і титри
          вийдуть простіші), переклад озвучки без OpenAI не вийде - обробка зупиниться з поверненням токенів; без Replicate - лише «якісна» озвучка.
        </Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ fontWeight: 800 }}>Закрити</Button></DialogActions>
    </Dialog>
  );
}
